'use client';

import { langHome, langPrefix } from '@/lib/locale-path';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
// The listing wizard is prefetched on intent (components/IntentLink.tsx).
import IntentLink from '@/components/IntentLink';
import { usePathname } from 'next/navigation';
import { Home, Plus } from 'lucide-react';
import { listingsPath, rentalsPath } from '@/lib/results-paging';
import NavbarControls from './NavbarControls';
import LanguageSwitcher from './LanguageSwitcher';
import AccountMenu from './AccountMenu';

interface GlobalNavbarProps {
  lang: string;
  customEnPath?: string;
  customFrPath?: string;
  hideActions?: boolean;
}

// Scrolled this far in one direction before the header reacts, so a thumb's
// small jitter does not make it flicker.
const SCROLL_STEP = 6;

// A form field someone is typing in - the same fields site.css gives the one
// thin focus line.
const FIELD =
  'input:not([type=checkbox],[type=radio],[type=range],[type=file],[type=hidden],[type=submit],[type=button],[type=reset],[type=color],[type=image]),textarea,select,[contenteditable="true"]';
const isField = (el: EventTarget | null): el is Element => el instanceof Element && el.matches(FIELD);

export default function GlobalNavbar({ lang, customEnPath, customFrPath, hideActions = false }: GlobalNavbarProps) {
  const pathname = usePathname();
  const sections = [
    { href: listingsPath(lang), label: lang === 'fr' ? 'Acheter' : 'Buy' },
    { href: rentalsPath(lang), label: lang === 'fr' ? 'Louer' : 'Rent' },
  ];
  const isFr = lang === 'fr';
  const navRef = useRef<HTMLElement>(null);
  const [hidden, setHidden] = useState(false);
  const hiddenRef = useRef(false);
  // Whether anything of the page is under the header yet (owner, 2026-09-24):
  // its edge fade shows only then, as on iOS and in Material. At the top of a
  // page it would only darken the first screen.
  const [scrolled, setScrolled] = useState(false);
  const scrolledRef = useRef(false);
  // A field in use on a phone (owner, 2026-09-24): the header goes at once and
  // stays away until the field is left. The keyboard takes half the screen,
  // the page shifts to show the field, and a header coming back on that small
  // movement sat right on top of what was being typed.
  const typingRef = useRef(false);

  // The owner's choice (2026-09-23): the header leaves while the page scrolls
  // down, to give the listings the screen, and comes back as soon as it
  // scrolls up. It stays while one of its menus is open or it has focus.
  useEffect(() => {
    const root = document.documentElement;
    let lastY = window.scrollY;
    let frame = 0;
    const show = (next: boolean) => {
      if (hiddenRef.current === !next) return;
      hiddenRef.current = !next;
      setHidden(!next);
      root.dataset.header = next ? 'shown' : 'hidden';
    };
    const update = () => {
      frame = 0;
      const nav = navRef.current;
      if (!nav) return;
      const y = window.scrollY;
      if (scrolledRef.current !== y > 0) {
        scrolledRef.current = y > 0;
        setScrolled(y > 0);
      }
      // Keyboard focus in it, or one of its menus open. A tap leaves focus on
      // the button it touched, which is not a reason to stay.
      const busy = !!nav.querySelector(':focus-visible, [aria-expanded="true"]');
      if (typingRef.current) {
        show(false);
        lastY = y;
      } else if (y <= nav.offsetHeight || busy) {
        show(true);
        lastY = y;
      } else if (y > lastY + SCROLL_STEP) {
        show(false);
        lastY = y;
      } else if (y < lastY - SCROLL_STEP) {
        show(true);
        lastY = y;
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const phone = window.matchMedia('(max-width: 767px)');
    const onFocusIn = (e: FocusEvent) => {
      if (!phone.matches || !isField(e.target) || navRef.current?.contains(e.target)) return;
      typingRef.current = true;
      show(false);
    };
    const onFocusOut = (e: FocusEvent) => {
      // Moving from one field to the next is still typing. Once done, the
      // header answers to the page again at once - at the top of the page
      // there is no scroll to bring it back.
      if (isField(e.target) && !isField(e.relatedTarget)) {
        typingRef.current = false;
        if (!frame) frame = requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    // A page opened part-way down (back, reload, an anchor) is already scrolled.
    frame = requestAnimationFrame(update);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (frame) cancelAnimationFrame(frame);
      delete root.dataset.header;
    };
  }, []);

  const revealOnFocus = () => {
    if (!hiddenRef.current) return;
    hiddenRef.current = false;
    setHidden(false);
    document.documentElement.dataset.header = 'shown';
  };

  return (
    <>
      <nav
        ref={navRef}
        onFocus={revealOnFocus}
        aria-label={isFr ? 'Navigation principale' : 'Main navigation'}
        className={`w-full h-16 sm:h-20 bar-surface sticky top-0 z-50 flex items-center justify-between px-2.5 sm:px-8 transition-transform duration-200 motion-reduce:transition-none ${hidden ? '-translate-y-full' : ''}`}
      >
        {/* The same edge as the tab bar's (bar-fade-*, site.css): an 8px fade
            under the header instead of a line, drawn only once the page has
            scrolled under it. Not drawn while the header is hidden above the
            screen, where it would be a strip at the top. */}
        {!hidden && scrolled && <span aria-hidden="true" className="bar-fade-down" />}
        <div className="flex items-center gap-4">
          <Link href={`${langHome(lang)}`} aria-label="Real Estate Channel" className="flex items-center gap-2 py-1 text-white hover:opacity-80 transition-opacity">
            <Home className="w-6 h-6 text-champagne" />
            <span className="font-display font-bold text-lg hidden sm:block tracking-tight gold-shine-text">Real Estate Channel</span>
          </Link>
          {/* Buy and Rent, as on Realtor.ca and Centris (rentals, 2026-09-24).
              A phone has them in the account menu: its header has no room. */}
          {!hideActions && (
            <div className="hidden md:flex items-center gap-1">
              {sections.map((s) => (
                <Link
                  key={s.href}
                  href={s.href}
                  aria-current={pathname?.startsWith(s.href) ? 'page' : undefined}
                  className="px-3 py-2 rounded-full text-sm font-semibold text-neutral-300 hover:text-white aria-[current=page]:text-champagne transition-colors"
                >
                  {s.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {!hideActions && (
            <>
              {/* Add Listing - Desktop & Mobile. Goes straight to the private-seller
                  wizard: the role-picker modal used to force an extra click before
                  anyone could start, and agencies get their own pitch inside step 1. */}
              <IntentLink
                id="btn-nav-add-listing-desktop"
                href={`${langPrefix(lang)}/add?type=private`}
                className="hidden md:flex items-center gap-2 bg-champagne text-black hover:bg-champagne/85 px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold transition shadow-md cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                {isFr ? 'Ajouter une annonce' : 'Add Listing'}
              </IntentLink>

              {/* No round "+" here on a phone: the bottom bar's raised button
                  is the same link, larger, and within reach of a thumb. Two of
                  them on one screen, forty pixels apart, read as two different
                  actions. The pill above is the desktop route, where there is
                  no bottom bar. */}

              {/* Favorites and Compare */}
              <NavbarControls lang={lang} />

              {/* Account menu (depends on who is signed in) */}
              <AccountMenu lang={lang} />
            </>
          )}

          {/* Language Switcher */}
          <LanguageSwitcher lang={lang} className="ml-1" customEnPath={customEnPath} customFrPath={customFrPath} />
        </div>
      </nav>
    </>
  );
}

